import { debug } from 'application/Shared/Utils';
import { evaluate } from 'cel-js';
import merge from 'deepmerge';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { templateInterpolation } from '../Dashboard/Helpers/templateInterpolation';
import { type ControlSchemaJson } from './Field.type';

/** Result object returned by the useConditionalProperties hook */
interface UseConditionalPropertiesResult {
    /** Indicates whether the field should be rendered based on the evaluated conditions */
    shouldRender: boolean;
    /** Modified control schema containing the evaluated states (only if input schema was provided) */
    control?: ControlSchemaJson;
}

/**
 * A custom hook that handles conditional rendering and property evaluation for form controls
 * based on Common Expression Language (CEL) expressions or boolean values.
 *
 * The hook watches form values and evaluates conditions to determine:
 * - Whether the field should be rendered
 * - If the field should be disabled
 * - If the field should be read-only
 *
 * @param {ControlSchemaJson} [auvikControl] - The control schema configuration object that may contain:
 *   - render: boolean | string - Determines if the field should be rendered (CEL expression or boolean)
 *   - disabled: boolean | string - Determines if the field should be disabled (CEL expression or boolean)
 *   - readOnly: boolean | string - Determines if the field should be read-only (CEL expression or boolean)
 *
 * @returns {UseConditionalPropertiesResult} An object containing:
 *   - shouldRender: boolean - Whether the field should be rendered
 *   - control?: ControlSchemaJson - Modified control schema with evaluated disabled and readOnly states
 *
 * @example
 * // Using with a boolean condition
 * const { shouldRender, control } = useConditionalProperties({
 *   render: true,
 *   disabled: false,
 *   readOnly: true
 * });
 *
 * @example
 * // Using with CEL expressions
 * const { shouldRender, control } = useConditionalProperties({
 *   render: "formValues.showField == true",
 *   disabled: "formValues.isDisabled == true",
 *   readOnly: "formValues.mode == 'view'"
 * });
 */
export const useConditionalProperties = (
    auvikControl?: ControlSchemaJson
): UseConditionalPropertiesResult => {
    const formContext = useFormContext();
    const formValues = formContext.watch(); // We are watching for all form values

    // Get the merged context from form values
    const context = useMemo(
        () => merge(formContext.formState.defaultValues as object, formValues),
        // We update the context on every value change
        [formContext.formState.defaultValues, formValues]
    );

    // Evaluate if the field should be rendered
    const shouldRender = useMemo(() => {
        const { render = true } = auvikControl || {};

        if (typeof render === 'boolean') {
            return render;
        }

        if (typeof render === 'string') {
            try {
                return Boolean(evaluate(render, context));
            } catch (error) {
                debug.error('Failed to evaluate CEL expression:', error);
                return false;
            }
        }

        return true;
    }, [auvikControl, context]);

    // Evaluate field disabled state
    const disabled = useMemo<boolean | string | undefined>(() => {
        const { disabled } = auvikControl || {};

        if (typeof disabled === 'boolean' || disabled === undefined) {
            return disabled;
        }

        if (typeof disabled === 'string') {
            try {
                return Boolean(evaluate(disabled, context));
            } catch (error) {
                debug.error('Failed to evaluate CEL expression:', error);
                return false;
            }
        }

        return false;
    }, [auvikControl, context]);

    // Evaluate field readOnly state
    const readOnly = useMemo<boolean | string | undefined>(() => {
        const { readOnly } = auvikControl || {};

        if (typeof readOnly === 'boolean' || readOnly === undefined) {
            return readOnly;
        }

        if (typeof readOnly === 'string') {
            try {
                return Boolean(evaluate(readOnly, context));
            } catch (error) {
                debug.error('Failed to evaluate CEL expression:', error);
                return false;
            }
        }

        return false;
    }, [auvikControl, context]);

    const helperText =
        templateInterpolation(auvikControl?.helperText, context) || undefined;

    // Return the new control object only if auvikControl exists
    const control = useMemo<undefined | ControlSchemaJson>(() => {
        if (!auvikControl) return undefined;

        return {
            ...auvikControl,
            disabled,
            helperText,
            // format: auvikControl.format,
            readOnly
        };
    }, [auvikControl, disabled, helperText, readOnly]);

    return { control, shouldRender };
};
